import {
  BookingForMail,
  BookingForName,
  ChevStateWrap,
  TravellerBookingForComboBox,
  TravellerCounterComboBox,
  TravellerCounterDropdown,
  TravellerCounterDropdownItem,
} from "./TravellingToWidget.style";

import { getSuggestionsInfo } from "@tm/insurance/api";
import { replaceHistory } from "@tm/insurance/utils";
import { AUTO_SUGGESTION_PAGE_SIZE, PARTNER } from "@tm/isomorphic-utils";
import { FlexComp, ReactPortalComp, SpanTag } from "@tm/static";
import { BlueChev } from "@tm/static/icons";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import { useCallback, useEffect, useRef, useState } from "react";
import FormattedText from "../FormattedText/FormattedText";
import TravellerEmailSelector from "../TravellerEmailSelector/TravellerEmailSelector";

interface TravellerBookingForWidgetProps {
  travellerBookingFor: any;
  captureCount: number;
  travCounterTitle?: {
    title: string;
  };
  setCaptureCount: React.Dispatch<React.SetStateAction<number>>;
}

const TravellerBookingForWidget = ({
  travellerBookingFor,
  captureCount,
  travCounterTitle,
  setCaptureCount,
}: TravellerBookingForWidgetProps) => {
  const [showTravCounterDropdown, setShowTravCounterDropdown] = useState(false);
  const [showTravBookingForDropdown, setShowTravBookingForDropdown] =
    useState(false);
  const [selectedBookingFor, setSelectedBookingFor] = useState({
    text: travellerBookingFor?.defaultSelected?.text || "",
    subText: travellerBookingFor?.defaultSelected?.subText || "",
    id: travellerBookingFor?.id || "",
  });
  const [displayOptions, setDisplayOptions] = useState({
    userOptions: travellerBookingFor?.searchDropDown?.userOptions || [],
    colleagueOptions:
      travellerBookingFor?.searchDropDown?.colleagueOptions || null,
  });
  const [searchValue, setSearchValue] = useState("");

  const travCounterDropdownRef = useRef<HTMLDivElement | null>(null);
  const device = useBreakpointV2();

  useEffect(() => {
    function handleClickOutsideTravCounter(event: MouseEvent) {
      if (
        travCounterDropdownRef.current &&
        !travCounterDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTravCounterDropdown(false);
      }
    }
    if (showTravCounterDropdown) {
      document.addEventListener("mousedown", handleClickOutsideTravCounter);
    } else {
      document.removeEventListener("mousedown", handleClickOutsideTravCounter);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideTravCounter);
    };
  }, [showTravCounterDropdown]);

  const handleCloseTravBookingForDropdown = () => {
    setShowTravBookingForDropdown(false);
    setDisplayOptions({
      userOptions: travellerBookingFor?.searchDropDown?.userOptions || [],
      colleagueOptions:
        travellerBookingFor?.searchDropDown?.colleagueOptions || null,
    });
  };

  const travBookingForDropdownRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (device !== "lg") return;
    function handleClickOutsideTravBookingFor(event: MouseEvent) {
      if (
        travBookingForDropdownRef.current &&
        !travBookingForDropdownRef.current?.contains(event.target as Node)
      ) {
        setShowTravBookingForDropdown(false);
      }
    }
    if (showTravBookingForDropdown) {
      document.addEventListener("mousedown", handleClickOutsideTravBookingFor);
    } else {
      document.removeEventListener(
        "mousedown",
        handleClickOutsideTravBookingFor
      );
    }
    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutsideTravBookingFor
      );
    };
  }, [showTravBookingForDropdown, device]);

  // Prepare dropdown items for traveller count selection
  const travellerCountOptions = Array.from({ length: 20 }, (_, i) => i + 1).map(
    (num) => (
      <TravellerCounterDropdownItem
        key={num}
        selected={num === captureCount}
        onClick={(e) => {
          e.stopPropagation();
          setCaptureCount(num);
          replaceHistory("travellers", num.toString());
          setShowTravCounterDropdown(false);
        }}
      >
        {num} Traveller{num !== 1 ? "s" : ""}
      </TravellerCounterDropdownItem>
    )
  );
  // TODO: Create a function for getting url params data where the params name needs to be configured in a constant
  const performSearch = useCallback(async (searchTerm: string) => {
    const response = await getSuggestionsInfo({
      pattern: searchTerm,
      pageSize: AUTO_SUGGESTION_PAGE_SIZE,
      partner: PARTNER.MYBIZ,
      query: {
        itineraryId:
          new URLSearchParams(window.location.search).get("itineraryId") || "",
      },
    });
    setDisplayOptions({
      userOptions: response?.data?.suggestions || [],
      colleagueOptions: null,
    });
  }, []);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  useEffect(() => {
    if (searchValue.length <= 2) {
      setDisplayOptions({
        userOptions: travellerBookingFor?.searchDropDown?.userOptions || [],
        colleagueOptions:
          travellerBookingFor?.searchDropDown?.colleagueOptions || null,
      });
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(searchValue);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchValue, performSearch]);

  const emailSelectorComp = () => {
    return (
      <TravellerEmailSelector
        label={travellerBookingFor?.searchDropDown?.label}
        description={travellerBookingFor?.searchDropDown?.description}
        options={displayOptions?.userOptions}
        onSelect={setSelectedBookingFor}
        onClose={handleCloseTravBookingForDropdown}
        colleagueOptions={displayOptions?.colleagueOptions}
        onSearch={onSearch}
      />
    );
  };

  return (
    <>
      <TravellerCounterComboBox
        ref={travCounterDropdownRef}
        onClick={() => setShowTravCounterDropdown((prev) => !prev)}
      >
        <FlexComp gap="8px">
          {travCounterTitle && (
            <SpanTag
              textTransform="uppercase"
              color={
                showTravCounterDropdown
                  ? `var(--tm-accent-color)`
                  : theme.greyText
              }
              fontWeight={"bold"}
              fontSize="xs"
            >
              <FormattedText value={travCounterTitle?.title} />
            </SpanTag>
          )}
          <ChevStateWrap open={showTravCounterDropdown}>
            <BlueChev />
          </ChevStateWrap>
        </FlexComp>
        <FlexComp>
          {captureCount} Traveller{captureCount !== 1 ? "s" : ""}
        </FlexComp>
        {showTravCounterDropdown && (
          <TravellerCounterDropdown>
            {travellerCountOptions}
          </TravellerCounterDropdown>
        )}
      </TravellerCounterComboBox>
      <TravellerBookingForComboBox
        ref={travBookingForDropdownRef}
        style={{ position: "relative" }}
      >
        <div
          onClick={() =>
            setShowTravBookingForDropdown(!showTravBookingForDropdown)
          }
        >
          <FlexComp gap="8px">
            {travellerBookingFor && (
              <SpanTag
                textTransform="uppercase"
                color={
                  showTravBookingForDropdown
                    ? `var(--tm-accent-color)`
                    : theme.greyText
                }
                fontWeight={"bold"}
                fontSize="xs"
              >
                <FormattedText value={travellerBookingFor?.title} />
              </SpanTag>
            )}
            <ChevStateWrap open={showTravBookingForDropdown}>
              <BlueChev />
            </ChevStateWrap>
          </FlexComp>
          <FlexComp flexDirection="column">
            <BookingForName>{selectedBookingFor?.text}</BookingForName>
            <BookingForMail>{selectedBookingFor?.subText}</BookingForMail>
          </FlexComp>
        </div>
        {showTravBookingForDropdown && device === "lg" && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              zIndex: 1000,
              width: "100%",
            }}
          >
            {emailSelectorComp()}
          </div>
        )}
        {showTravBookingForDropdown && device === "sm" && (
          <ReactPortalComp>{emailSelectorComp()}</ReactPortalComp>
        )}
      </TravellerBookingForComboBox>
    </>
  );
};

export default TravellerBookingForWidget;
